import React, { useState, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import ClassSectionFilter from '../Students/parts/ClassSectionFilter';

interface ClassData {
  id: string;
  class_name: string;
  sections: string[];
}

interface StudentData {
  student_id: number;
  user_id: number;
  name: string;
  class_id: number;
  section: string | null;
  roll_no: number | string;
}

interface ResetResponse {
  status: string;
  message: string;
  data?: {
    class_id: number;
    class_name: string;
    section: string | null;
    students_count: number;
    students: StudentData[];
  };
}

const BulkPasswordReset: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [resetting, setResetting] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>('');
  const [resetResult, setResetResult] = useState<ResetResponse | null>(null);

  const handleClassesLoaded = (loadedClasses: ClassData[]) => {
    setClasses(loadedClasses);
  };

  const selectedClassHasSections = (): boolean => {
    if (!selectedClass) return false;
    const classData = classes.find((c) => c.id === selectedClass);
    return classData ? Boolean(classData.sections && classData.sections.length > 0) : false;
  };

  const canResetPasswords = (): boolean => {
    if (!selectedClass || !newPassword.trim()) return false;
    const hasSections = selectedClassHasSections();
    return hasSections ? selectedSection !== null : true;
  };

  const getClassNameById = (classId: number): string => {
    const classData = classes.find((c) => c.id === String(classId));
    return classData ? classData.class_name : `Class ID: ${classId}`;
  };

  const handleClassChange = (classId: string | null) => {
    setSelectedClass(classId);
    setSelectedSection(null);
    setResetResult(null);
  };

  const handleSectionChange = (section: string | null) => {
    setSelectedSection(section);
    setResetResult(null);
  };

  const resetClassPasswords = async (): Promise<void> => {
    if (!canResetPasswords()) return;

    setResetting(true);
    setResetResult(null);

    try {
      const requestBody = {
        class_id: selectedClass,
        section: selectedSection,
        new_password: newPassword.trim()
      };

      const response = await apiClient.post('/admin/reset-class-passwords', requestBody);
      const data: ResetResponse = response.data;

      if (data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: data.message,
          life: 4000
        });
        setResetResult(data);
        setNewPassword('');
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: data.message || 'Failed to reset passwords',
          life: 5000
        });
      }
    } catch (err: any) {
      console.error('Failed to reset passwords:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Network Error',
        detail: err.response?.data?.message || 'An error occurred while resetting passwords',
        life: 5000
      });
    } finally {
      setResetting(false);
    }
  };

  const resetForm = (): void => {
    setSelectedClass(null);
    setSelectedSection(null);
    setNewPassword('');
    setResetResult(null);
  };

  const selectedClassData = classes.find(c => c.id === selectedClass);

  return (
    <>
      <Toast ref={toast} position="top-right" />

      {/* Loading overlay for reset operation */}
      {resetting && (
        <div style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ 
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            padding: '40px',
            textAlign: 'center',
            maxWidth: '400px',
            width: '90%'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #fee2e2',
              borderTop: '4px solid #dc2626',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px auto'
            }}></div>
            <h2 style={{ 
              color: '#dc2626', 
              fontSize: '18px', 
              fontWeight: '600',
              margin: '0 0 8px 0'
            }}>
              Resetting Passwords
            </h2>
            <p style={{ 
              color: '#6b7280', 
              fontSize: '14px',
              margin: 0
            }}>
              Please wait while we update the passwords...
            </p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}

      <div style={{
        minHeight: '100vh',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        {/* Header */}
        <GlobalHeaderTitle
          title="Student Password Reset"
          subtitle="Reset passwords for all students in a class or section"
          icon="pi pi-lock"
          buttons={[
            {
              label: "Reset Passwords",
              icon: "pi pi-refresh",
              onClick: resetClassPasswords,
              disabled: !canResetPasswords() || resetting,
              style: { 
                background: canResetPasswords() && !resetting 
                  ? 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)' 
                  : undefined 
              }
            },
            {
              label: "Reset Form",
              icon: "pi pi-replay",
              onClick: resetForm,
              disabled: resetting,
              outlined: true
            }
          ]}
        />

        {/* Class Section Filter */}
        <ClassSectionFilter
          selectedClass={selectedClass}
          selectedSection={selectedSection}
          onClassChange={handleClassChange}
          onSectionChange={handleSectionChange}
          onClassesLoaded={handleClassesLoaded}
        />

        {/* Password Input */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.12)',
        }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '600',
            color: '#1e40af',
            marginBottom: '8px',
            letterSpacing: '0.02em'
          }}>
            New Password
          </label>
          <InputText
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setResetResult(null);
            }}
            placeholder="Enter new password for all students"
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '13px'
            }}
          />
        </div>

        {/* Selection Summary */}
        {selectedClass && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.12)',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
              borderRadius: '8px',
              border: '1px solid rgba(59, 130, 246, 0.15)'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <i className="pi pi-info-circle" style={{ fontSize: '18px', color: 'white' }}></i>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '2px' }}>
                  Selected:
                </div>
                <div style={{ fontSize: '15px', fontWeight: '600', color: '#1e40af' }}>
                  {selectedClassData?.class_name}
                  {selectedSection && <span> - Section {selectedSection}</span>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reset Result */}
        {resetResult && resetResult.data && (
          <div style={{
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            width: '100%'
          }}>
            {/* Success Header */}
            <div style={{
              padding: '20px',
              background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.1) 0%, rgba(16, 185, 129, 0.08) 100%)',
              borderBottom: '1px solid rgba(5, 150, 105, 0.15)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className="pi pi-check-circle" style={{ fontSize: '20px', color: 'white' }}></i>
                </div>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#059669', margin: 0 }}>
                    Password Reset Successful
                  </h3>
                  <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0 0' }}>
                    All student passwords have been updated
                  </p>
                </div>
              </div>

              {/* Stats Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                <div style={{
                  textAlign: 'center',
                  padding: '12px',
                  background: 'white',
                  borderRadius: '8px',
                  border: '1px solid rgba(5, 150, 105, 0.15)'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#059669' }}>
                    {resetResult.data.students_count}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                    Students Updated
                  </div>
                </div>
                <div style={{
                  textAlign: 'center',
                  padding: '12px',
                  background: 'white',
                  borderRadius: '8px',
                  border: '1px solid rgba(5, 150, 105, 0.15)'
                }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#059669' }}>
                    {resetResult.data.class_name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                    Class
                  </div>
                </div>
                <div style={{
                  textAlign: 'center',
                  padding: '12px',
                  background: 'white',
                  borderRadius: '8px',
                  border: '1px solid rgba(5, 150, 105, 0.15)'
                }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#059669' }}>
                    {resetResult.data.section || 'All'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                    Section
                  </div>
                </div>
              </div>
            </div>

            {/* Students List */}
            <div style={{ padding: '20px' }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#1e40af',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <i className="pi pi-users" style={{ fontSize: '14px' }}></i>
                Updated Students
              </div>
              <div style={{
                maxHeight: '400px',
                overflowY: 'auto',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}>
                {resetResult.data.students.map((student, index) => (
                  <div
                    key={student.student_id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      background: index % 2 === 0 ? '#f8fafc' : 'white',
                      borderBottom: index < resetResult.data!.students.length - 1 ? '1px solid #e5e7eb' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'white'
                      }}>
                        {student.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                          {student.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          Roll: {student.roll_no}
                        </div>
                      </div>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      textAlign: 'right'
                    }}>
                      {getClassNameById(student.class_id)}
                      {student.section ? ` - ${student.section}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!selectedClass && (
          <div style={{
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            width: '100%'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px',
              minHeight: '400px'
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
                boxShadow: '0 6px 18px rgba(59, 130, 246, 0.2)',
              }}>
                <i className="pi pi-lock" style={{ fontSize: '25px', color: 'white' }}></i>
              </div>
              <h3 style={{
                margin: 0,
                color: '#1e40af',
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '8px',
                textAlign: 'center'
              }}>
                Select a Class to Reset Passwords
              </h3>
              <p style={{
                margin: 0,
                color: '#64748b',
                fontSize: '14px',
                fontWeight: '500',
                textAlign: 'center',
                maxWidth: '400px',
                lineHeight: 1.6
              }}>
                Choose a class and optionally a section from the filter above, then enter the new password to reset passwords for all students
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BulkPasswordReset;